'use client';

import { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { FiX, FiMail, FiMapPin, FiSend, FiCheckCircle, FiLinkedin, FiTwitter } from 'react-icons/fi';
import { Button, AppLink, SocialLink, Form } from '@/shared/ui';
import type { IconType } from 'react-icons';
import { contactMock } from '@/shared/store/mocks/contact.mock';
import type { ContactSocialItem } from '@/shared/store/mocks/contact.mock';
import styles from './contact-modal.module.scss';
import { useContactForm } from './useContactForm';

const SOCIAL_ICONS: Record<ContactSocialItem['id'], IconType> = {
    email: FiMail,
    linkedin: FiLinkedin,
    twitter: FiTwitter,
};

const CLOSE_DURATION_MS = 520;

export interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
    const { formData, fieldErrors, handleFieldChange, status, errorMessage, handleSubmit, isSubmitDisabled } =
        useContactForm();
    const [isClosing, setIsClosing] = useState(false);
    const [showOpenClass, setShowOpenClass] = useState(false);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { contactInfo, socials, form: formConfig } = contactMock;

    const handleClose = () => {
        if (isClosing) return;
        setIsClosing(true);
        setShowOpenClass(false);
        closeTimeoutRef.current = setTimeout(() => {
            onClose();
            setIsClosing(false);
            closeTimeoutRef.current = null;
        }, CLOSE_DURATION_MS);
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) handleClose();
    };

    useEffect(() => {
        if (!isOpen) return;
        let raf2: number;
        let raf3: number;
        const raf1 = requestAnimationFrame(() => {
            setIsClosing(false);
            setShowOpenClass(false);
            raf2 = requestAnimationFrame(() => {
                raf3 = requestAnimationFrame(() => setShowOpenClass(true));
            });
        });
        return () => {
            cancelAnimationFrame(raf1);
            if (raf2 != null) cancelAnimationFrame(raf2);
            if (raf3 != null) cancelAnimationFrame(raf3);
        };
    }, [isOpen]);

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        };
    }, []);

    if (!isOpen && !isClosing) return null;

    const openClass = showOpenClass ? styles.overlayOpen : '';
    const wrapperOpenClass = showOpenClass ? styles.wrapperOpen : '';

    return (
        <>
            <div className={clsx(styles.overlay, openClass)} onClick={handleOverlayClick} aria-hidden />
            <div
                className={clsx(styles.wrapper, wrapperOpenClass)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-modal-title"
            >
                <div className={styles.modal}>
                    <div className={styles.card}>
                        <Button
                            type="button"
                            className={styles.closeBtn}
                            onClick={handleClose}
                            aria-label="Close"
                            endIcon={<FiX size={20} />}
                        />

                        <div className={clsx(styles.content, styles.contentAnimated)}>
                            <h2 id="contact-modal-title" className={styles.title}>
                                {formConfig.title}
                            </h2>
                            <p className={styles.subtitle}>{formConfig.subtitle}</p>

                            <div className={styles.grid}>
                                <div>
                                    <h3 className={styles.sideTitle}>{formConfig.contactSideTitle}</h3>
                                    <div className={styles.contactList}>
                                        <AppLink href={`mailto:${contactInfo.email}`} className={styles.contactLink}>
                                            <div className={styles.contactItem}>
                                                <div className={styles.contactIcon}>
                                                    <FiMail size={18} />
                                                </div>
                                                <div>
                                                    <div className={styles.contactLabel}>Email</div>
                                                    <div className={styles.contactValue}>{contactInfo.email}</div>
                                                </div>
                                            </div>
                                        </AppLink>
                                        <div className={styles.contactItem}>
                                            <div className={styles.contactIcon}>
                                                <FiMapPin size={18} />
                                            </div>
                                            <div>
                                                <div className={styles.contactLabel}>Location</div>
                                                <div className={styles.contactValue}>{contactInfo.location}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.socialsTitle}>{formConfig.socialsTitle}</div>
                                    <div className={styles.socials}>
                                        {socials.map(s => (
                                            <SocialLink
                                                key={s.id}
                                                href={s.href}
                                                icon={SOCIAL_ICONS[s.id]}
                                                label={s.label}
                                                size={20}
                                                className={styles.socialLink}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div
                                    className={clsx(styles.formColumn, status === 'success' && styles.formColumnSuccess)}
                                >
                                    {status === 'success' && (
                                        <div className={styles.successBanner} role="status">
                                            <FiCheckCircle size={48} className={styles.successIcon} />
                                            {formConfig.successMessage}
                                        </div>
                                    )}
                                    <h3 className={styles.sideTitle}>{formConfig.formSideTitle}</h3>
                                    <Form
                                        fields={formConfig.fields}
                                        values={formData}
                                        errors={fieldErrors}
                                        onChange={handleFieldChange}
                                        onSubmit={handleSubmit}
                                        honeypotName={formConfig.honeypotName}
                                        status={status}
                                        errorMessage={errorMessage}
                                        submitLabel={formConfig.submitLabel}
                                        sendingLabel={formConfig.sendingLabel}
                                        isSubmitDisabled={isSubmitDisabled}
                                        submitEndIcon={<FiSend size={18} />}
                                        classNames={{
                                            form: styles.form,
                                            honeypot: styles.honeypot,
                                            fieldRoot: styles.fieldRoot,
                                            fieldRootError: styles.fieldRootError,
                                            input: styles.input,
                                            textarea: styles.textarea,
                                            error: styles.fieldError,
                                            submitBtn: styles.submitBtn,
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.decorTop} aria-hidden />
                        <div className={styles.decorBottom} aria-hidden />
                    </div>
                </div>
            </div>
        </>
    );
}
